import { useCallback, useMemo, useState } from 'react';
import TimeLowerLimitConfig from './TimeConfigModules/TimeLowerLimitConfig';
import { Checkbox, Dropdown, ComboBox, DefaultButton, Stack, TooltipHost, TextField, Text } from '@fluentui/react';
import { ContentSection } from '../../../../components/LayoutElements';
import { TimeConfigKeys } from '../../../../constants/TimeConfigKeys';
import { WorkdaySelector } from './TimeConfigModules/WorkdaysSelector';
import { ApiCalls } from '../../../../api/api';
import { useConfigValuesState } from '../../../../hooks/useConfigValue';
import { TimeUserFeatures } from '../../../../api/generated/data-contracts';
import { FunctionOr } from '../../../../types/FunctionOr';
import { unwrap } from '../../../../helpers/unwrap';
import { format } from '../../../../helpers/date';
import { EnhancedComboBoxOption } from '../../../../types/EnhancedComboBoxOption';
import { GridInput } from '../../../../types/GridInput';
import { Granularity } from '../../../../types/Granularity';
import { PercentageInputTypeDisclaimer } from './TimeConfigModules/PercentageInputTypeDisclaimer';

export function AdminTimeConfig() {
    const {
        initialData: {
            configuration: {
                dateTimeFormat,
                dateFormat,
                shortDateFormat,
                gridView,
                gridInputType,
                onlyAssignedSearch,
                workTypesEnabled,
                enableCoachmarks,
                showPlanned,
                workDayBoundaries,
                workDayMinutesInterval,
                timesheetRAGsEnabled,
                nativeChartsEnabled,
                onlyAllowTimeSheetWithinTaskStartEnd,
                workDayAMPM,
                allowCellComment,
                allowCellFlag,
                allowTimeSheetComment,
                timeEntriesImportEnabled,
                autoCreateNewUsers,
                enableSubmitPeriodOnImport,
                autoCreationDisabled,
                lockProcessedTimeEntries,
                remainingWorkWritable,
                approval: {
                    projectManagerApproval: { enabled: projectManagerApprovalEnabled },
                },
            },
        },
        setConfigValue,
    } = useConfigValuesState();

    const [demoDataLoaded, setDemoDataLoaded] = useState<boolean>(false);
    const [editaleDateTimeFormat, setEditaleDateTimeFormat] = useState(dateTimeFormat);
    const [editableShortDateFormat, setEditableShortDateFormat] = useState(shortDateFormat);
    const setter = useCallback(
        <K extends keyof TimeUserFeatures>(key: K, value: FunctionOr<TimeUserFeatures[K]>) => {
            setConfigValue(config => {
                return {
                    ...config,
                    [key]: unwrap(value, config[key]),
                };
            });
        },
        [setConfigValue],
    );

    const saveDateformat = useCallback(
        (value: string) => {
            setter('dateTimeFormat', value);
        },
        [setter],
    );

    const saveShortDateformat = useCallback(
        (value: string) => {
            setter('shortDateFormat', value);
        },
        [setter],
    );

    const setOnlyAssigned = useCallback(
        (value: boolean) => {
            setter('onlyAssignedSearch', value);
        },
        [setter],
    );

    const setWorkTypeEnabled = useCallback(
        (value: boolean) => {
            setter('workTypesEnabled', value);
        },
        [setter],
    );

    const setEnableCoachmarks = useCallback(
        (value: boolean) => {
            setter('enableCoachmarks', value);
        },
        [setter],
    );

    const setDisablePeriodAutoCreation = useCallback(
        (value: boolean) => {
            console.log('setDisablePeriodAutoCreation', !value);
            setter('autoCreationDisabled', !value);
        },
        [setter],
    );

    const setDisableAutoUserCreation = useCallback(
        (value: boolean) => {
            setter('autoCreateNewUsers', value);
        },
        [setter],
    );

    const saveDatetimeformat = useCallback(
        (value: string) => {
            setter('dateTimeFormat', value);
        },
        [setter],
    );

    const saveGridInputType = useCallback(
        (value: string) => {
            setter('gridInputType', value);
        },
        [setter],
    );

    const saveShowPlanned = useCallback(
        (value: boolean) => {
            setter('showPlanned', value);
        },
        [setter],
    );

    const saveGridView = useCallback(
        (value: string) => {
            setter('gridView', value);
        },
        [setter],
    );

    const saveWorkDayBoundaries = useCallback(
        (value: boolean) => {
            setter('workDayBoundaries', value);
        },
        [setter],
    );

    const saveWorkDayMinutesInterval = useCallback(
        (value: number) => {
            setter('workDayMinutesInterval', value);
        },
        [setter],
    );

    const saveTimesheeRAGsEnabled = useCallback(
        (value: boolean) => {
            setter('timesheetRAGsEnabled', value);
        },
        [setter],
    );

    const saveNativeChartsEnabled = useCallback(
        (value: boolean) => {
            setter('nativeChartsEnabled', value);
        },
        [setter],
    );

    const saveOnlyAllowTimeSheetWithinTaskStartEnd = useCallback(
        (value: boolean) => {
            setter('onlyAllowTimeSheetWithinTaskStartEnd', value);
        },
        [setter],
    );

    const saveWorkDayAMPM = useCallback(
        (value: boolean) => {
            setter('workDayAMPM', value);
        },
        [setter],
    );

    const saveCellComment = useCallback(
        (value: boolean) => {
            setter('allowCellComment', value);
        },
        [setter],
    );

    const saveCellFlag = useCallback(
        (value: boolean) => {
            setter('allowCellFlag', value);
        },
        [setter],
    );

    const saveTimeSheetComment = useCallback(
        (value: boolean) => {
            setter('allowTimeSheetComment', value);
        },
        [setter],
    );

    const saveEnableTimeEntriesImport = useCallback(
        (value: boolean) => {
            setter('timeEntriesImportEnabled', value);
        },
        [setter],
    );

    const saveSubmitPeriodOnImport = useCallback(
        (value: boolean) => {
            setter('enableSubmitPeriodOnImport', value);
        },
        [setter],
    );

    const externallyApprovalLock = useCallback(
        (value: boolean) => {
            setter('lockProcessedTimeEntries', value);
        },
        [setter],
    );

    const saveRemainingWorkWritable = useCallback(
        (value: boolean) => {
            setter('remainingWorkWritable', value);
        },
        [setter],
    );

    const downloadTeamsApp = useCallback(async () => {
        try {
            const response = await ApiCalls.downloadMsteamsappList({ format: 'blob' }).then(response => response.data);

            const url = window.URL.createObjectURL(response);

            const link = document.createElement('a');
            link.href = url;
            // Name the file
            link.setAttribute('download', 'Time For Teams App.zip');

            document.body.appendChild(link);

            link.click();

            link.parentNode?.removeChild(link);
        } catch (error) {
            console.error('There was an error downloading the file!', error);
        }
    }, []);

    const setAllowTelemitry = value => {
        throw new Error(TimeConfigKeys.AllowTelemitry + ' needs to be implemented');
    };

    const gridInputOptions = useMemo((): EnhancedComboBoxOption<GridInput>[] => {
        return [
            {
                key: 'percent',
                text: 'Percent' + (projectManagerApprovalEnabled ? ' (Unavailable when project approval is enabled)' : ''),
                disabled: gridView === 'days' || projectManagerApprovalEnabled,
            },
            { key: 'hours', text: 'Hours' },
        ];
    }, [gridView, projectManagerApprovalEnabled]);

    const granularityOptions = useMemo((): EnhancedComboBoxOption<Granularity>[] => {
        return [
            { key: 'days', text: 'Days', disabled: gridInputType === 'percent' },
            {
                key: 'period',
                text: 'Period' + (projectManagerApprovalEnabled ? ' (Unavailable when project approval is enabled)' : ''),
                disabled: projectManagerApprovalEnabled,
            },
        ];
    }, [gridInputType, projectManagerApprovalEnabled]);

    return (
        <ContentSection>
            <Stack tokens={{ childrenGap: 32 }}>
                <TimeLowerLimitConfig />
                <Stack tokens={{ childrenGap: 16 }}>
                    <ComboBox
                        label="Register time as (grid input)"
                        selectedKey={gridInputType}
                        onChange={(e, option) => saveGridInputType(option.key as string)}
                        options={gridInputOptions}
                    />
                    <PercentageInputTypeDisclaimer />
                    <ComboBox
                        label="View granularity"
                        selectedKey={gridView}
                        onChange={(e, option) => saveGridView(option.key as string)}
                        options={granularityOptions}
                    />

                    <Checkbox onChange={(e, value) => saveShowPlanned(value)} checked={showPlanned} label="Show planned/remaining/actual work" />
                    {showPlanned && (
                        <Stack styles={{ root: { marginLeft: 20 } }}>
                            <Checkbox
                                onChange={(e, value) => saveRemainingWorkWritable(value)}
                                checked={remainingWorkWritable}
                                label="Allow editing remaining work"
                            />
                        </Stack>
                    )}
                    <WorkdaySelector />
                    <Checkbox onChange={(e, value) => saveWorkDayBoundaries(value)} checked={workDayBoundaries} label="Workday start/end time" />
                    <Checkbox onChange={(e, value) => saveWorkDayAMPM(value)} checked={workDayAMPM} label="Workday start/end time AM/PM" />
                    <Dropdown
                        label="Workday start/end time minutes interval"
                        defaultSelectedKey={workDayMinutesInterval}
                        options={[
                            { key: 1, text: '1' },
                            { key: 5, text: '5' },
                            { key: 10, text: '10' },
                            { key: 15, text: '15' },
                            { key: 20, text: '20' },
                            { key: 30, text: '30' },
                        ]}
                        onChange={(e, value) => saveWorkDayMinutesInterval(Number(value.key))}
                    />
                    <Checkbox onChange={(e, value) => setOnlyAssigned(value)} defaultChecked={onlyAssignedSearch} label="Only search assigned tasks" />
                    <Checkbox
                        onChange={(e, value) => saveTimeSheetComment(value)}
                        defaultChecked={allowTimeSheetComment}
                        label="Allow Timesheet Submit Comment"
                    />
                    <Checkbox onChange={(e, value) => saveCellComment(value)} defaultChecked={allowCellComment} label="Allow Cell Comment" />
                    <Checkbox onChange={(e, value) => saveCellFlag(value)} defaultChecked={allowCellFlag} label="Allow Cell Flag" />
                    <Checkbox
                        onChange={(e, value) => saveOnlyAllowTimeSheetWithinTaskStartEnd(value)}
                        defaultChecked={onlyAllowTimeSheetWithinTaskStartEnd}
                        label="Only allow time sheet within task start and end"
                    />
                    <Checkbox
                        onChange={(e, value) => setDisablePeriodAutoCreation(value)}
                        defaultChecked={!autoCreationDisabled}
                        label="Enable Auto Period Creation"
                    />
                    <Checkbox
                        onChange={(e, value) => setDisableAutoUserCreation(value)}
                        defaultChecked={autoCreateNewUsers}
                        label="Enable Auto User Creation"
                    />
                    <Checkbox onChange={(e, value) => setWorkTypeEnabled(value)} defaultChecked={workTypesEnabled} label="Enable WorkType" />
                    <Checkbox onChange={(e, value) => setEnableCoachmarks(value)} defaultChecked={enableCoachmarks} label="Enable Coachmarks" />
                    <Checkbox
                        onChange={(e, value) => setAllowTelemitry(value)}
                        //
                        disabled
                        // defaultChecked={uiCtx.allowTelemitry}
                        label="Allow Telemetry"
                    />
                    <Checkbox
                        onChange={(e, value) => saveTimesheeRAGsEnabled(value)}
                        checked={timesheetRAGsEnabled}
                        label="Enable RAG indicators in timesheet"
                    />
                    <Checkbox onChange={(e, value) => saveNativeChartsEnabled(value)} checked={nativeChartsEnabled} label="Enable Native Charts" />
                    <Checkbox
                        onChange={(e, value) => saveEnableTimeEntriesImport(value)}
                        checked={timeEntriesImportEnabled}
                        label="Enable Time Entries Import"
                    />
                    <Checkbox
                        onChange={(e, value) => saveSubmitPeriodOnImport(value)}
                        defaultChecked={enableSubmitPeriodOnImport}
                        label="Enable Add Approval on Time Entry Import"
                    />
                    <Checkbox
                        onChange={(e, value) => externallyApprovalLock(value)}
                        checked={lockProcessedTimeEntries}
                        label="Lock grid cells and approvals upon external approval"
                    />
                    <Stack>
                        <TooltipHost
                            tooltipProps={{
                                onRenderContent: () => (
                                    <>The date and time format to be shown where a timestamp is needed. Ex. on the Approver's page in the column "Submitted"</>
                                ),
                            }}
                        >
                            <Stack>
                                {/* TODO: Since right now the changes are not saved on the backend, the inputs are set to disabled. Consider remove that when the update works corretly */}
                                <TextField
                                    label="Datetime format"
                                    placeholder="DD/MM/YYYY HH:mm:ss"
                                    onChange={(e, v) => {
                                        setEditaleDateTimeFormat(v);
                                    }}
                                    onBlur={e => {
                                        saveDatetimeformat(e.target.value);
                                    }}
                                    defaultValue={editaleDateTimeFormat}
                                    disabled
                                ></TextField>
                                <Text variant="small">Ex. {format(new Date(), editaleDateTimeFormat)}</Text>
                            </Stack>
                        </TooltipHost>
                        <TooltipHost tooltipProps={{ onRenderContent: () => <>The date format to be shown.</> }}>
                            <Stack>
                                <TextField
                                    label="Normal date format"
                                    placeholder="DD/MM/YYYY"
                                    onChange={(e, v) => {
                                        setEditaleDateTimeFormat(v);
                                    }}
                                    onBlur={e => {
                                        saveDateformat(e.target.value);
                                    }}
                                    defaultValue={dateFormat}
                                    disabled
                                ></TextField>
                                <Text variant="small">Ex. {format(new Date(), dateFormat)}</Text>
                            </Stack>
                        </TooltipHost>
                        <TooltipHost
                            tooltipProps={{
                                onRenderContent: () => (
                                    <>The date format to be shown when a short date is displayed. Ex. on the Time page when you click on the day name</>
                                ),
                            }}
                        >
                            <Stack>
                                <TextField
                                    label="Short date format"
                                    placeholder="D MMM"
                                    value={editableShortDateFormat}
                                    onChange={(e, v) => {
                                        setEditableShortDateFormat(v);
                                    }}
                                    onBlur={e => {
                                        saveShortDateformat(editableShortDateFormat);
                                    }}
                                    disabled
                                />
                                <Text variant="small">Ex. {format(new Date(), editableShortDateFormat)}</Text>
                            </Stack>
                        </TooltipHost>
                    </Stack>
                </Stack>
                <Stack tokens={{ childrenGap: 16 }}>
                    <DefaultButton text="Download MS Teams App" onClick={downloadTeamsApp} />
                    <DefaultButton
                        text="Download PowerPPM Integration"
                        href={'https://projectumcdnstorage.blob.core.windows.net/timepackages/common/TimeforTeams_Managed.zip'}
                    />
                    <DefaultButton
                        text="Download DevOps Integration"
                        href={'https://projectumcdnstorage.blob.core.windows.net/timepackages/common/TimeforTeamsDevOps_Managed.zip'}
                    />
                    <DefaultButton
                        text="Download Project for the web Integration"
                        href={'https://projectumcdnstorage.blob.core.windows.net/timepackages/common/TimeforTeamsProjectfortheweb_managed.zip'}
                    />
                    <DefaultButton
                        text="Load Demo Data"
                        disabled={demoDataLoaded}
                        onClick={() => {
                            setDemoDataLoaded(true);
                            ApiCalls.createDemoDataForTrial({})
                                .then(() => {
                                    alert('Demo data have been created.');
                                    document.location.reload();
                                })
                                .catch(() => {
                                    alert('Please delete existing demo data and data first.');
                                });
                        }}
                    />
                </Stack>
            </Stack>
        </ContentSection>
    );
}
